import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Edit2, ExternalLink, Save, Eye, X, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { InsightArticle } from '../insights/InsightPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyArticle = () => ({
  slug: '', published: false,
  canonical_url: '', page_title: '', description: '',
  og_title: '', og_description: '', linkedin_cover: '',
  category: '', title: '', standfirst: '',
  author: '', reading_time: '', issue: '',
  hero_image: '', hero_alt: '', hero_caption: '',
  evidence_label: '', evidence_headline: '', evidence_summary: '',
  evidence_metrics: [],
  executive_lead: '',
  sections: [],
  perspective: '',
  checklist_title: 'Investor due diligence',
  checklist_items: [],
  bottom_line: '',
  sources: [],
});

const REQUIRED_FIELDS = ['slug', 'title', 'standfirst', 'canonical_url'];

const Section = ({ title, children }) => (
  <div className="bg-white/[0.02] border border-white/10 rounded-lg p-5 space-y-3">
    <h4 className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold">{title}</h4>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, placeholder = '', type = 'text', required = false, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.14em] text-white/50 block mb-1.5">
      {label}{required && <span className="text-[#D4AF37]"> *</span>}
    </label>
    <input
      type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} data-testid={testid}
      className="w-full bg-black/25 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#D4AF37]/40 transition-colors"
    />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 4, placeholder = '', testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.14em] text-white/50 block mb-1.5">{label}</label>
    <textarea
      value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows}
      placeholder={placeholder} data-testid={testid}
      className="w-full bg-black/25 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#D4AF37]/40 font-mono transition-colors"
    />
  </div>
);

export const InsightsTab = ({ getStoredPassword }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {mode: 'new'|'edit', article, originalSlug?}
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/insights?password=${getStoredPassword()}`);
      if (res.ok) setItems(await res.json());
    } catch { }
    setLoading(false);
  }, [getStoredPassword]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ mode: 'new', article: emptyArticle() });
  const startEdit = async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/insights/${slug}?password=${getStoredPassword()}`);
      if (res.ok) {
        const article = await res.json();
        setEditing({ mode: 'edit', article, originalSlug: slug });
      }
    } catch { toast.error('Failed to load article'); }
  };
  const cancel = () => { setEditing(null); setPreview(false); };

  const validate = (article) => {
    for (const f of REQUIRED_FIELDS) {
      if (!article[f] || String(article[f]).trim() === '') {
        toast.error(`Missing required field: ${f}`);
        return false;
      }
    }
    if (!/^[a-z0-9-]+$/.test(article.slug)) {
      toast.error('Slug must be lowercase letters, digits, hyphens only');
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!editing) return;
    if (!validate(editing.article)) return;
    setSaving(true);
    try {
      const body = JSON.stringify(editing.article);
      const url = editing.mode === 'new'
        ? `${API_URL}/api/admin/insights?password=${getStoredPassword()}`
        : `${API_URL}/api/admin/insights/${editing.originalSlug}?password=${getStoredPassword()}`;
      const res = await fetch(url, {
        method: editing.mode === 'new' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' }, body,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(editing.mode === 'new' ? 'Article created' : 'Article saved');
      setEditing(null);
      setPreview(false);
      await load();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    setSaving(false);
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete article "${slug}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/insights/${slug}?password=${getStoredPassword()}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); await load(); }
      else { toast.error('Delete failed'); }
    } catch { toast.error('Delete failed'); }
  };

  // ----- List view -----
  if (!editing) {
    return (
      <div className="space-y-6" data-testid="insights-tab">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg text-white">Insights</h3>
            <p className="text-white/45 text-xs mt-1">Executive Property Investment Insight articles.</p>
          </div>
          <button onClick={startNew} data-testid="new-insight-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#D4AF37] text-black text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#B8941E] transition-colors">
            <Plus className="w-3.5 h-3.5" /> New article
          </button>
        </div>
        {loading ? (
          <div className="text-white/50 text-sm py-8 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm py-8 text-center border border-dashed border-white/10 rounded-lg">No articles yet.</div>
        ) : (
          <div className="space-y-2">
            {items.map((a) => (
              <div key={a.slug} data-testid={`insight-row-${a.slug}`}
                className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-lg p-3 hover:border-[#D4AF37]/25 transition-colors">
                <div className={`w-1.5 h-8 rounded-full flex-shrink-0 ${a.published ? 'bg-[#D4AF37]' : 'bg-white/15'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm truncate">{a.title}</span>
                    <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded ${a.published ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'bg-white/5 text-white/40'}`}>
                      {a.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <div className="text-white/40 text-[11px] mt-0.5 truncate">/{a.slug} · {a.author || 'no author'}</div>
                </div>
                <a href={`/insights/${a.slug}`} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-md hover:bg-white/5 text-white/50 hover:text-[#D4AF37]" title="Open on site">
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button onClick={() => startEdit(a.slug)} className="p-2 rounded-md hover:bg-white/5 text-white/50 hover:text-white" title="Edit"
                  data-testid={`edit-insight-${a.slug}`}>
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => remove(a.slug)} className="p-2 rounded-md hover:bg-red-500/10 text-white/40 hover:text-red-400" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ----- Preview mode -----
  if (preview) {
    return (
      <div className="space-y-3" data-testid="insight-preview-shell">
        <div className="flex items-center justify-between">
          <button onClick={() => setPreview(false)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/70 text-xs">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to edit
          </button>
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37]">Editorial preview (with workspace controls)</div>
        </div>
        <div className="rounded-lg overflow-hidden border border-white/10 max-h-[80vh] overflow-y-auto">
          <InsightArticle article={editing.article} isEditorial={true} />
        </div>
      </div>
    );
  }

  // ----- Edit / New form -----
  const a = editing.article;
  const patch = (fields) => setEditing({ ...editing, article: { ...a, ...fields } });
  const patchList = (key, list) => patch({ [key]: list });

  return (
    <div className="space-y-4" data-testid="insight-editor">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap sticky top-0 z-10 bg-[#0a1a14] pb-3 -mx-1 px-1">
        <button onClick={cancel} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/70 text-xs">
          <ArrowLeft className="w-3.5 h-3.5" /> All articles
        </button>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-white/60 cursor-pointer">
            <input type="checkbox" checked={a.published} onChange={(e) => patch({ published: e.target.checked })}
              className="accent-[#D4AF37]" data-testid="insight-published-toggle" />
            Published
          </label>
          <button onClick={() => setPreview(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/15 text-white/80 text-xs hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
            data-testid="insight-preview-btn">
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md bg-[#D4AF37] text-black text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#B8941E] disabled:opacity-50"
            data-testid="insight-save-btn">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Header + SEO */}
      <Section title="Article header · SEO">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Slug (URL segment)" required value={a.slug} onChange={(v) => patch({ slug: v })} placeholder="the-liquidity-illusion" testid="insight-slug" />
          <Field label="Canonical URL" required value={a.canonical_url} onChange={(v) => patch({ canonical_url: v })} placeholder="https://black-starpi.com/insights/..." />
          <Field label="Page title" value={a.page_title} onChange={(v) => patch({ page_title: v })} placeholder="Title | Black Star PI" />
          <Field label="Meta description" value={a.description} onChange={(v) => patch({ description: v })} />
          <Field label="OG title" value={a.og_title} onChange={(v) => patch({ og_title: v })} />
          <Field label="OG description" value={a.og_description} onChange={(v) => patch({ og_description: v })} />
          <Field label="LinkedIn cover URL (1200×627)" value={a.linkedin_cover} onChange={(v) => patch({ linkedin_cover: v })} />
        </div>
      </Section>

      {/* Byline & identity */}
      <Section title="Article identity">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Category (eyebrow)" value={a.category} onChange={(v) => patch({ category: v })} placeholder="Investment Structure · Regulatory Intelligence" />
          <Field label="Title" required value={a.title} onChange={(v) => patch({ title: v })} testid="insight-title" />
          <Field label="Standfirst / subtitle" required value={a.standfirst} onChange={(v) => patch({ standfirst: v })} />
          <Field label="Author" value={a.author} onChange={(v) => patch({ author: v })} />
          <Field label="Reading time" value={a.reading_time} onChange={(v) => patch({ reading_time: v })} placeholder="6 min read" />
          <Field label="Issue" value={a.issue} onChange={(v) => patch({ issue: v })} placeholder="Issue 01 · September 2026" />
        </div>
      </Section>

      {/* Hero */}
      <Section title="Hero image">
        <Field label="Hero image URL" value={a.hero_image} onChange={(v) => patch({ hero_image: v })} placeholder="/insights/.../hero.jpg or https://..." />
        <Field label="Hero alt text" value={a.hero_alt} onChange={(v) => patch({ hero_alt: v })} />
        <Field label="Photo caption / attribution" value={a.hero_caption} onChange={(v) => patch({ hero_caption: v })} />
      </Section>

      {/* Evidence panel */}
      <Section title="Evidence panel">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Section label" value={a.evidence_label} onChange={(v) => patch({ evidence_label: v })} placeholder="The 30 day reality" />
          <Field label="Headline" value={a.evidence_headline} onChange={(v) => patch({ evidence_headline: v })} />
        </div>
        <TextArea label="Summary" value={a.evidence_summary} onChange={(v) => patch({ evidence_summary: v })} rows={2} />
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] uppercase tracking-[0.14em] text-white/50">Metrics</label>
            <button onClick={() => patchList('evidence_metrics', [...(a.evidence_metrics || []), { value: '', label: '', width: 100, colour: 'forest' }])}
              className="text-[#D4AF37] text-xs">+ Add metric</button>
          </div>
          <div className="space-y-2">
            {(a.evidence_metrics || []).map((m, i) => (
              <div key={i} className="grid grid-cols-[80px_1fr_80px_100px_28px] gap-2 items-center">
                <input value={m.value} onChange={(e) => { const l=[...a.evidence_metrics]; l[i]={...m, value:e.target.value}; patchList('evidence_metrics', l); }}
                  placeholder="10%" className="bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
                <input value={m.label} onChange={(e) => { const l=[...a.evidence_metrics]; l[i]={...m, label:e.target.value}; patchList('evidence_metrics', l); }}
                  placeholder="NAV redeemable" className="bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
                <input type="number" value={m.width} min="0" max="100" onChange={(e) => { const l=[...a.evidence_metrics]; l[i]={...m, width:Number(e.target.value)}; patchList('evidence_metrics', l); }}
                  className="bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
                <select value={m.colour} onChange={(e) => { const l=[...a.evidence_metrics]; l[i]={...m, colour:e.target.value}; patchList('evidence_metrics', l); }}
                  className="bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm">
                  <option value="forest">Forest</option><option value="gold">Gold</option>
                </select>
                <button onClick={() => patchList('evidence_metrics', a.evidence_metrics.filter((_,x)=>x!==i))} className="text-red-400/70 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Body */}
      <Section title="Article body">
        <TextArea label="Executive opening (lead paragraph)" value={a.executive_lead} onChange={(v) => patch({ executive_lead: v })} rows={2} />
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] uppercase tracking-[0.14em] text-white/50">Sections</label>
            <button onClick={() => patchList('sections', [...(a.sections || []), { heading: '', body_html: '' }])} className="text-[#D4AF37] text-xs">+ Add section</button>
          </div>
          <div className="space-y-3">
            {(a.sections || []).map((s, i) => (
              <div key={i} className="border border-white/10 rounded-md p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input value={s.heading} onChange={(e) => { const l=[...a.sections]; l[i]={...s, heading:e.target.value}; patchList('sections', l); }}
                    placeholder="Section heading" className="flex-1 bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
                  <button onClick={() => patchList('sections', a.sections.filter((_,x)=>x!==i))} className="text-red-400/70 hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
                <textarea value={s.body_html} onChange={(e) => { const l=[...a.sections]; l[i]={...s, body_html:e.target.value}; patchList('sections', l); }}
                  rows={6} placeholder="<p>Body HTML — use &lt;p&gt; and &lt;ul&gt; tags</p>"
                  className="w-full bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-xs font-mono" />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Sidebar */}
      <Section title="Sidebar">
        <TextArea label="Black Star perspective (pull quote)" value={a.perspective} onChange={(v) => patch({ perspective: v })} rows={3} />
        <Field label="Checklist title" value={a.checklist_title} onChange={(v) => patch({ checklist_title: v })} />
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] uppercase tracking-[0.14em] text-white/50">Investor due diligence points</label>
            <button onClick={() => patchList('checklist_items', [...(a.checklist_items || []), ''])} className="text-[#D4AF37] text-xs">+ Add</button>
          </div>
          <div className="space-y-2">
            {(a.checklist_items || []).map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={it} onChange={(e) => { const l=[...a.checklist_items]; l[i]=e.target.value; patchList('checklist_items', l); }}
                  className="flex-1 bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
                <button onClick={() => patchList('checklist_items', a.checklist_items.filter((_,x)=>x!==i))} className="text-red-400/70 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Bottom line */}
      <Section title="Bottom line">
        <TextArea label="Bottom-line conclusion" value={a.bottom_line} onChange={(v) => patch({ bottom_line: v })} rows={3} />
      </Section>

      {/* Sources */}
      <Section title="Sources">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] uppercase tracking-[0.14em] text-white/50">Cited sources</label>
            <button onClick={() => patchList('sources', [...(a.sources || []), { citation_html: '', url: '', date: '' }])} className="text-[#D4AF37] text-xs">+ Add source</button>
          </div>
          <div className="space-y-2">
            {(a.sources || []).map((s, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_180px_28px] gap-2 items-center">
                <input value={s.citation_html} onChange={(e) => { const l=[...a.sources]; l[i]={...s, citation_html:e.target.value}; patchList('sources', l); }}
                  placeholder='Reuters, "…"' className="bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
                <input value={s.url} onChange={(e) => { const l=[...a.sources]; l[i]={...s, url:e.target.value}; patchList('sources', l); }}
                  placeholder="https://..." className="bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
                <input value={s.date} onChange={(e) => { const l=[...a.sources]; l[i]={...s, date:e.target.value}; patchList('sources', l); }}
                  placeholder="3 September 2026" className="bg-black/25 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
                <button onClick={() => patchList('sources', a.sources.filter((_,x)=>x!==i))} className="text-red-400/70 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
};

export default InsightsTab;
